import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Package, ChevronDown, ChevronRight, Loader2, Search, Calendar,
  FileText, Building2, Mail, ExternalLink, RefreshCw,
} from 'lucide-react';
import { api, useAuthStore } from '../lib/auth';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PostNL Distributielijsten archive viewer.
 *
 * The data here is produced by the IMAP rule engine (see `mail_api.py`):
 * a rule with `archive_type="postnl_distribution"` matches incoming mail
 * (subject regex + sender regex), grabs every ZIP attachment, explodes it
 * into individual PDFs on disk and writes a `postnl_distribution_archives`
 * doc the API serves below. We render it as a per-firm feed: one row per
 * archive, expand to see the PDF list.
 */
export default function PostnlDistributionArchive() {
  const me = useAuthStore((s) => s.user);
  const [firms, setFirms] = useState([]);
  const [firmId, setFirmId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [summary, setSummary] = useState([]);
  // Sağlık durumu — son archive tarihi, aktif kural, son eşleşen mail
  const [health, setHealth] = useState(null);

  // ── load firms (super admin sees all, firm admin sees own) ────────
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/firms/');
        const list = res.data?.data || res.data?.firms || [];
        setFirms(list);
        if (!firmId && me?.role !== 'SUPER_ADMIN' && me?.firm_id) {
          setFirmId(me.firm_id);
        }
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line
  }, []);

  const reloadSummary = async () => {
    try {
      const res = await api.get('/postnl-distribution/firms-summary');
      setSummary(res.data?.data || []);
    } catch { /* silent */ }
    try {
      const h = await api.get('/postnl-distribution/health-status');
      setHealth(h.data?.data || null);
    } catch { /* silent */ }
  };

  const reload = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (firmId) params.set('firm_id', firmId);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (q.trim()) params.set('q', q.trim());
      params.set('limit', '200');
      const res = await api.get(`/postnl-distribution?${params}`);
      setItems(res.data?.data || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Liste yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); reloadSummary(); /* eslint-disable-next-line */ }, [firmId, dateFrom, dateTo]);
  useEffect(() => {
    const t = setTimeout(reload, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const firmName = (id) => firms.find((f) => f.id === id)?.name || (id || '—');

  const openPdf = async (archiveId, filename) => {
    try {
      const token = useAuthStore.getState().accessToken;
      const enc = encodeURIComponent(filename);
      const res = await axios.get(`${API_URL}/api/postnl-distribution/${archiveId}/pdfs/${enc}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      // We intentionally don't revoke the URL immediately: the browser tab
      // needs it alive to render the PDF. Modern browsers GC blobs when
      // the tab closes.
    } catch {
      toast.error('PDF açılamadı');
    }
  };

  const totalPdfs = useMemo(() => items.reduce((acc, a) => acc + (a.pdf_count || 0), 0), [items]);

  return (
    <div className="p-4 max-w-7xl mx-auto space-y-4" data-testid="postnl-archive-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Package className="h-6 w-6 text-blue-500" />
        <div>
          <h1 className="text-xl font-semibold">PostNL Distributielijsten Arşivi</h1>
          <p className="text-xs text-slate-500">
            Mail kurallarıyla otomatik açılan ZIP eklerinin PDF listesi — firma + tarih bazlı.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => { reload(); reloadSummary(); }} className="ml-auto" data-testid="postnl-refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      {me?.role === 'SUPER_ADMIN' && (
        <S3BackfillCard />
      )}

      {/* Sağlık durumu — son archive ne zaman geldi, sync OK mi? */}
      {health && <ArchiveHealthBanner health={health} firmName={firmName} />}

      {/* Per-firm summary tiles */}
      {summary.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {summary.map((s) => (
            <button
              key={s.firm_id}
              onClick={() => setFirmId((cur) => (cur === s.firm_id ? '' : s.firm_id))}
              className={`text-left p-3 rounded-lg border transition ${firmId === s.firm_id ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/40'}`}
              data-testid={`postnl-firm-tile-${s.firm_id}`}
            >
              <div className="flex items-center gap-1.5 text-xs font-semibold">
                <Building2 className="h-3.5 w-3.5" />
                {firmName(s.firm_id)}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                <span className="font-bold text-slate-800 dark:text-slate-100">{s.archives}</span> arşiv ·{' '}
                <span className="font-bold text-slate-800 dark:text-slate-100">{s.pdfs}</span> PDF
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card className="p-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <div>
            <label className="text-[10px] uppercase text-slate-400 block mb-0.5">Firma</label>
            <Select value={firmId || '__all__'} onValueChange={(v) => setFirmId(v === '__all__' ? '' : v)}>
              <SelectTrigger className="h-8 text-xs" data-testid="postnl-firm-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {me?.role === 'SUPER_ADMIN' && <SelectItem value="__all__">Tümü</SelectItem>}
                {firms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-400 block mb-0.5">Başlangıç</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 text-xs" data-testid="postnl-date-from" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-400 block mb-0.5">Bitiş</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 text-xs" data-testid="postnl-date-to" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-400 block mb-0.5">Ara (konu / ZIP / gönderen)</label>
            <div className="relative">
              <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="distributielijst..." className="pl-7 h-8 text-xs" data-testid="postnl-search" />
            </div>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 mt-2">
          {total} arşiv · {totalPdfs} PDF (sayfalı sonuçta görünen)
        </div>
      </Card>

      {/* Archive feed */}
      {loading && items.length === 0 && (
        <Card className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" /></Card>
      )}
      {!loading && items.length === 0 && (
        <Card className="p-8 text-center text-slate-400 text-sm">
          Henüz arşiv yok. Mail Arşiv Kuralları sayfasından bir <b>PostNL Distributielijsten</b> kuralı ekleyin
          ve "Tümünü Çalıştır" butonuna basın.
        </Card>
      )}

      {items.map((arc) => {
        const isOpen = expanded.has(arc.id);
        return (
          <Card key={arc.id} className="overflow-hidden" data-testid={`postnl-arc-${arc.id}`}>
            <button
              onClick={() => toggle(arc.id)}
              className="w-full p-3 flex items-center gap-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/40"
              data-testid={`postnl-arc-toggle-${arc.id}`}
            >
              {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-blue-500 text-[10px]">{firmName(arc.firm_id)}</Badge>
                  <Badge variant="outline" className="text-[10px] flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {arc.primary_date || '—'}
                  </Badge>
                  <Badge variant="outline" className="text-[10px]">{arc.pdf_count} PDF</Badge>
                  {arc.tag_label && <Badge variant="outline" className="text-[10px]">#{arc.tag_label}</Badge>}
                  {arc.source_zip_s3_uploaded && (
                    <Badge
                      variant="outline"
                      className="text-[10px] border-blue-300 bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700/60"
                      title="ZIP ve PDF'ler S3'e de yüklendi"
                      data-testid={`postnl-s3-badge-${arc.id}`}
                    >
                      ☁ S3
                    </Badge>
                  )}
                </div>
                <div className="text-sm font-semibold mt-1 truncate">{arc.subject}</div>
                <div className="text-[11px] text-slate-500 truncate flex items-center gap-2 mt-0.5">
                  <Mail className="h-3 w-3" />
                  {arc.from_name ? `${arc.from_name} <${arc.from_addr}>` : arc.from_addr}
                  <span className="text-slate-300">·</span>
                  <FileText className="h-3 w-3" />
                  {arc.source_zip_filename}
                </div>
              </div>
            </button>
            {isOpen && (
              <div className="border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 p-3">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {(arc.pdfs || []).map((pdf) => (
                    <button
                      key={pdf.filename}
                      onClick={() => openPdf(arc.id, pdf.filename)}
                      className="flex items-center gap-2 p-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-blue-400 text-left"
                      data-testid={`postnl-pdf-${arc.id}-${pdf.filename}`}
                    >
                      <FileText className="h-4 w-4 text-rose-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-mono truncate">{pdf.original_filename || pdf.filename}</div>
                        <div className="text-[10px] text-slate-400">
                          {prettyBytes(pdf.size)}
                          {pdf.date_in_filename && <span className="ml-1">· {pdf.date_in_filename}</span>}
                        </div>
                      </div>
                      <ExternalLink className="h-3 w-3 text-slate-400 shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function prettyBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 102.4) / 10} KB`;
  return `${Math.round(n / 104857.6) / 10} MB`;
}

// ── S3 geri-yükleme (backfill) kartı ──────────────────────────────────────
// Şub 09, 2026 — S3 toggle açılmadan önce arşivlenmiş mail attachment'ları
// ve PostNL ZIP/PDF'lerini batch'ler halinde S3'e yükler. Her batch 50 kayıt.
// Haz 13, 2026 — "Kayıp Olanları Temizle" butonu eklendi: binary'leri fiziksel
// olarak silinmiş ama s3_uploaded flag'i set edilmemiş eski kayıtlar için.
function S3BackfillCard() {
  const [stats, setStats] = useState(null);
  const [running, setRunning] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [errBox, setErrBox] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get('/mail/archive/s3-migrate/status');
      setStats(res.data?.data || null);
    } catch { /* sessiz */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const startBatch = useCallback(async () => {
    setRunning(true);
    setErrBox(null);
    try {
      let totals = { mail_done: 0, postnl_zip_done: 0, postnl_pdf_done: 0, mail_err: 0, postnl_zip_err: 0, postnl_pdf_err: 0, errors: [] };
      for (let i = 0; i < 100; i++) {
        const res = await api.post('/mail/archive/s3-migrate/run?batch=50');
        const r = res.data?.data || {};
        totals.mail_done += r.mail_done || 0;
        totals.postnl_zip_done += r.postnl_zip_done || 0;
        totals.postnl_pdf_done += r.postnl_pdf_done || 0;
        totals.mail_err += r.mail_err || 0;
        totals.postnl_zip_err += r.postnl_zip_err || 0;
        totals.postnl_pdf_err += r.postnl_pdf_err || 0;
        if (Array.isArray(r.errors)) totals.errors = totals.errors.concat(r.errors).slice(0, 30);
        setProgress({ ...totals, batch: i + 1 });
        const did = (r.mail_done || 0) + (r.postnl_zip_done || 0) + (r.postnl_pdf_done || 0);
        if (did === 0) break;
      }
      const totalErr = totals.mail_err + totals.postnl_zip_err + totals.postnl_pdf_err;
      const totalDone = totals.mail_done + totals.postnl_zip_done + totals.postnl_pdf_done;
      if (totalDone > 0) {
        toast.success(`${totalDone} dosya S3'e yüklendi`);
      }
      if (totalErr > 0) {
        toast.error(`${totalErr} dosya yüklenemedi (binary'leri silinmiş). "Kayıp Olanları Temizle" butonunu kullanın.`);
        setErrBox({ total: totalErr, samples: totals.errors.slice(0, 5) });
      }
      await refresh();
    } catch (e) {
      toast.error(`Migration hatası: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRunning(false);
    }
  }, [refresh]);

  const cleanupMissing = useCallback(async () => {
    if (!window.confirm('Binary verisi silinmiş (S3\'e yüklenemeyecek) tüm kayıtlar "kayıp" olarak işaretlenecek. Devam?')) return;
    setCleaning(true);
    try {
      const res = await api.post('/mail/archive/s3-migrate/cleanup-missing');
      const r = res.data?.data || {};
      toast.success(`${r.total_marked || 0} kayıp kayıt işaretlendi (mail: ${r.mail_marked || 0}, ZIP: ${r.postnl_zip_marked || 0}, PDF: ${r.postnl_pdf_marked || 0})`);
      setErrBox(null);
      await refresh();
    } catch (e) {
      toast.error(`Temizlik hatası: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setCleaning(false);
    }
  }, [refresh]);

  if (!stats) return null;
  const total = stats.total_pending || 0;
  const missing = stats.total_missing || 0;
  if (total === 0 && !progress && missing === 0) {
    return (
      <Card className="p-3 bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800/60">
        <div className="text-xs text-emerald-700 dark:text-emerald-300 flex items-center gap-2">
          {`☁ Tüm mail/PostNL arşivleri S3'te`}
        </div>
      </Card>
    );
  }
  if (total === 0 && missing > 0) {
    return (
      <Card className="p-3 bg-slate-50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-700">
        <div className="text-xs text-slate-700 dark:text-slate-300 flex items-center gap-2">
          {`☁ Tüm mevcut arşivler S3'te · ${missing} kayıt fiziksel olarak kayıp (orijinal binary silinmiş)`}
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-3 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800/60" data-testid="s3-backfill-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-amber-800 dark:text-amber-200 flex items-center gap-2">
            {`☁ S3'e bekleyen eski arşivler`}
          </div>
          <div className="text-[11px] text-amber-700 dark:text-amber-300 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
            <span><b>{stats.mail_archive_pending}</b> mail attachment</span>
            <span><b>{stats.postnl_zip_pending}</b> PostNL ZIP</span>
            <span><b>{stats.postnl_pdf_pending}</b> PostNL PDF</span>
            <span className="text-amber-600 dark:text-amber-400">— toplam {total}</span>
            {missing > 0 && (
              <span className="text-rose-600 dark:text-rose-400">· {missing} kayıp (binary yok)</span>
            )}
          </div>
          {progress && (
            <div className="text-[10px] text-amber-700 dark:text-amber-300 mt-1.5">
              Batch #{progress.batch} · Yüklendi: {progress.mail_done} mail + {progress.postnl_zip_done} ZIP + {progress.postnl_pdf_done} PDF
              {(progress.mail_err + progress.postnl_zip_err + progress.postnl_pdf_err) > 0 && (
                <span className="ml-1 text-rose-600 dark:text-rose-400">
                  · {progress.mail_err + progress.postnl_zip_err + progress.postnl_pdf_err} hata
                </span>
              )}
            </div>
          )}
          {errBox && (
            <div className="text-[10px] text-rose-700 dark:text-rose-400 mt-1.5 p-2 bg-rose-50 dark:bg-rose-950/40 rounded border border-rose-200 dark:border-rose-800">
              <div className="font-semibold">{errBox.total} kayıt yüklenemedi — orijinal dosya bulunamadı:</div>
              {errBox.samples.map((s, i) => (
                <div key={i} className="font-mono truncate">{s}</div>
              ))}
              <div className="mt-1 italic">"Kayıp Olanları Temizle" butonu ile sayaçtan düşürebilirsiniz.</div>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <Button
            size="sm"
            onClick={startBatch}
            disabled={running || cleaning || total === 0}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="s3-backfill-start-btn"
          >
            {running ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Yükleniyor…</>
            ) : (
              <>S3&apos;e Yükle</>
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={cleanupMissing}
            disabled={running || cleaning || total === 0}
            className="border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-700 dark:text-rose-300"
            data-testid="s3-backfill-cleanup-btn"
            title="Binary verisi silinmiş (yüklenemeyecek) kayıtları sayaçtan düşür"
          >
            {cleaning ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Temizleniyor…</>
            ) : (
              <>Kayıp Olanları Temizle</>
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ── Sağlık durumu banner'ı ───────────────────────────────────────────────
// "stale" durumda: son archive 14+ gün önce → muhtemelen mail gelmiyor veya
// rule eşleşmiyor. Operatöre konkret bilgi gösteriyoruz ki teşhis kolay olsun.
function ArchiveHealthBanner({ health, firmName }) {
  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };
  const fmtDateOnly = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' }); }
    catch { return iso; }
  };

  const { status, last_archive_date, days_since_last_archive, active_rules, latest_matching_mail, accounts } = health;

  // healthy: silent banner (yeşil küçük bilgi)
  if (status === 'healthy') {
    return (
      <Card className="p-2.5 bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800/60">
        <div className="text-xs text-emerald-700 dark:text-emerald-300 flex flex-wrap items-center gap-2">
          <span className="font-semibold">✓ Akış sağlıklı</span>
          <span>· Son arşiv: <b>{fmtDateOnly(last_archive_date)}</b> ({days_since_last_archive} gün önce)</span>
          <span>· {active_rules.length} aktif kural</span>
        </div>
      </Card>
    );
  }

  // no_rules
  if (status === 'no_rules') {
    return (
      <Card className="p-3 bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800/60">
        <div className="text-sm font-semibold text-rose-800 dark:text-rose-200">⚠ Aktif kural yok</div>
        <div className="text-xs text-rose-700 dark:text-rose-300 mt-1">
          PostNL distribution mail'lerini yakalayan kural tanımlanmamış. Mail Sync &gt; Arşiv Kuralları menüsünden ekleyin.
        </div>
      </Card>
    );
  }

  // stale / no_data
  const isStale = status === 'stale';
  return (
    <Card className="p-3 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800/60" data-testid="archive-health-banner">
      <div className="flex items-start gap-3">
        <div className="text-xl">⚠</div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-amber-800 dark:text-amber-200">
            {isStale
              ? `Akış durmuş gibi görünüyor — son arşiv ${days_since_last_archive} gün önce (${fmtDateOnly(last_archive_date)})`
              : 'Henüz hiç arşiv yok'}
          </div>
          <div className="text-[11px] text-amber-700 dark:text-amber-300 mt-1.5 space-y-1">
            {latest_matching_mail ? (
              <div>
                📬 Son eşleşen mail: <b>{fmtDate(latest_matching_mail.date)}</b>
                {' · '}{latest_matching_mail.from_addr}
                <div className="font-mono text-[10px] truncate ml-5 opacity-70">"{latest_matching_mail.subject}"</div>
              </div>
            ) : (
              <div>📭 Kural pattern'ine uyan hiçbir mail bulunamadı.</div>
            )}
            {accounts.length > 0 && (
              <div>
                🔄 Mail sync hesapları:{' '}
                {accounts.map((a, i) => (
                  <span key={a.id}>
                    {i > 0 && ' · '}
                    <span className="font-mono">{a.email_address}</span>
                    {' '}({a.last_sync_status === 'ok' ? '✓' : '⚠'} {fmtDate(a.last_sync_at)})
                  </span>
                ))}
              </div>
            )}
            {active_rules.length > 0 && (
              <div>
                🎯 Aktif kurallar:{' '}
                {active_rules.map((r, i) => (
                  <span key={r.id}>
                    {i > 0 && ' · '}
                    <b>{r.label}</b>{' '}
                    <span className="font-mono text-[10px]">
                      [from: {r.from_pattern || '*'} | subj: {r.subject_pattern || '*'}]
                    </span>
                  </span>
                ))}
              </div>
            )}
            {isStale && (
              <div className="mt-2 p-2 bg-amber-100/60 dark:bg-amber-900/40 rounded text-[10.5px]">
                <b>Olası sebep:</b> Mail gönderici (info@hyk.be vb.) bu hesaba artık mail göndermiyor, ya da subject pattern değişmiş.
                Mail kutusunu kontrol edip kuralı güncelleyin.
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

